import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import {
  ChevronLeft,
  Factory,
  Home,
  MessageSquareText,
  MoreHorizontal,
  ShieldAlert,
  ShieldCheck,
  Store,
  UserRound,
  Wrench,
} from 'lucide-react'
import { type NavKey } from '@/roles/sv/nav'
import { isOmsPathActive, omsLabel, OMS_MOBILE_NAV, type OmsLocale } from '@/features/oms/nav'
import { cn } from '@/shared/utils/cn'
import { Badge, OmsNavIcon, UiIcon } from '@/shared/ui'
import { isUrgent } from '@/features/claim/data'
import { useClaimStore } from '@/features/claim/store'

// 키보드가 뜨지 않는 입력(체크박스·파일·스위치 등)은 제외.
const NON_TEXT_INPUT = new Set([
  'checkbox',
  'radio',
  'file',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'image',
  'hidden',
])

function isTextEntry(el: Element | null) {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable || el instanceof HTMLTextAreaElement) return true
  return el instanceof HTMLInputElement && !NON_TEXT_INPUT.has(el.type)
}

/** 입력칸에 포커스가 있는 동안(=키보드가 떠 있는 동안) true. */
function useTextEntryFocus() {
  const [typing, setTyping] = useState(false)
  useEffect(() => {
    const sync = () => setTyping(isTextEntry(document.activeElement))
    // focusout 순간엔 다음 포커스 대상이 아직 정해지지 않아 한 틱 뒤에 확인한다.
    const onFocusOut = () => window.setTimeout(sync, 0)
    document.addEventListener('focusin', sync)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', sync)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])
  return typing
}

interface BottomTabBarProps {
  active?: NavKey
  onNavigate?: () => void
  omsMode?: {
    activePath: string
    badges?: Record<string, number>
    locale: OmsLocale
    onClose: () => void
    onNavigate: (path: string, title: string) => void
  }
}

export function BottomTabBar({ active, onNavigate, omsMode }: BottomTabBarProps) {
  const { pathname } = useLocation()
  const section =
    pathname === '/qsc/home' || ['insp', 'hist', 'act', 'tpl'].includes(active ?? '')
      ? 'qsc'
      : pathname === '/prm/home' ||
          ['svstore', 'prm', 'construction', 'visitplan', 'sales', 'store', 'chgreq'].includes(
            active ?? '',
          )
        ? 'prm'
        : pathname.startsWith('/qa/')
          ? 'qa'
          : pathname.startsWith('/as')
            ? 'as'
            : pathname.startsWith('/claim')
              ? 'claim'
              : pathname === '/comm' || ['voc', 'notice', 'vote', 'qna'].includes(active ?? '')
                ? 'comm'
                : pathname === '/more' || active === 'set'
                  ? 'all'
                  : 'home'
  // 더보기를 누르면 바가 옆으로 넘어가 2페이지(AS·클레임·소통·MY)를 보여준다.
  // 2페이지 화면에 있으면 자동으로 2페이지, 다른 화면으로 가면 1페이지로 돌아온다.
  const onExtPage = ['as', 'claim', 'comm', 'all'].includes(section)
  const [ext, setExt] = useState(onExtPage)
  useEffect(() => setExt(onExtPage), [onExtPage, pathname])
  const urgentClaims = useClaimStore(
    (s) => s.claims.filter((c) => isUrgent(c.grade) && !c.checkedAt).length,
  )
  const tabs = [
    { key: 'qsc', label: 'QSC', path: '/qsc/home', icon: ShieldCheck, color: 'text-[#3183F5]' },
    { key: 'prm', label: 'PRM', path: '/prm/home', icon: Store, color: 'text-[#26977C]' },
    { key: 'home', label: '홈', path: '/home', icon: Home, color: 'text-[#3183F5]' },
    { key: 'qa', label: 'QA', path: '/qa/home', icon: Factory, color: 'text-[#9276EE]' },
  ]
  const extTabs = [
    { key: 'as', label: 'AS', path: '/as', icon: Wrench, badge: 0 },
    { key: 'claim', label: '클레임', path: '/claim', icon: ShieldAlert, badge: urgentClaims },
    { key: 'comm', label: '소통', path: '/comm', icon: MessageSquareText, badge: 0 },
    { key: 'all', label: 'MY', path: '/more', icon: UserRound, badge: 0 },
  ]
  // 입력 중엔 키보드 위로 밀려 올라오지 않게 숨긴다. 컴포넌트는 남겨 2페이지 상태를 유지한다.
  const typing = useTextEntryFocus()
  if (typing) return null
  if (omsMode) {
    const omsLocale = omsMode.locale
    const qscLabel = omsLocale === 'en' ? 'QSC' : omsLocale === 'zh-TW' ? 'QSC' : 'QSC'
    return (
      <nav
        data-qsc-chrome="true"
        className="mobile-bottom-nav grid shrink-0 grid-cols-5 border-t border-border-subtle bg-surface lg:hidden"
      >
        <button
          type="button"
          onClick={omsMode.onClose}
          className="flex min-w-0 flex-col items-center justify-center gap-1 text-preset-helper font-semibold text-text-subtle transition hover:text-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        >
          <UiIcon.ChevronLeft aria-hidden className="size-5" />
          <span className="max-w-full truncate">{qscLabel}</span>
        </button>
        {OMS_MOBILE_NAV.map((item) => {
          const selected = isOmsPathActive(omsMode.activePath, item.path)
          const badgeCount = omsMode.badges?.[item.k] ?? 0
          const label = omsLabel(item, omsLocale)
          return (
            <button
              key={item.k}
              type="button"
              onClick={() => omsMode.onNavigate(item.path, label)}
              className={cn(
                'flex min-w-0 flex-col items-center justify-center gap-1 text-preset-helper font-semibold transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset',
                selected ? 'text-brand' : 'text-text-subtle hover:text-text',
              )}
            >
              <span className="relative">
                <OmsNavIcon name={item.icon} className="size-5" />
                <Badge
                  count={badgeCount}
                  className="absolute -right-[10px] -top-[9px] h-[16px] min-w-[16px] px-[4px] text-[9px] ring-2 ring-surface"
                />
              </span>
              <span className="max-w-full truncate">{label}</span>
            </button>
          )
        })}
      </nav>
    )
  }

  const tabClass =
    'flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand'

  return (
    <nav
      data-qsc-chrome="true"
      className="mobile-bottom-nav shrink-0 overflow-hidden border-t border-border-subtle bg-surface lg:hidden"
    >
      <div
        className={cn(
          'flex w-[200%] transition-transform duration-300 ease-out motion-reduce:transition-none',
          ext && '-translate-x-1/2',
        )}
      >
        <div inert={ext} className="grid w-1/2 grid-cols-5">
          {tabs.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              onClick={onNavigate}
              aria-current={section === item.key ? 'page' : false}
              className={cn(tabClass, section === item.key ? item.color : 'text-slate-400')}
            >
              <item.icon
                aria-hidden
                className="size-5"
                strokeWidth={section === item.key ? 2.4 : 1.8}
              />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setExt(true)}
            aria-label="더보기 메뉴 열기"
            className={cn(tabClass, 'text-slate-400')}
          >
            <span className="relative">
              <MoreHorizontal aria-hidden className="size-5" strokeWidth={1.8} />
              {urgentClaims ? (
                <span className="absolute -right-1 -top-0.5 size-2 rounded-full bg-danger ring-2 ring-surface" />
              ) : null}
            </span>
            <span>더보기</span>
          </button>
        </div>

        <div inert={!ext} className="grid w-1/2 grid-cols-5">
          <button
            type="button"
            onClick={() => setExt(false)}
            aria-label="기본 메뉴로 돌아가기"
            className={cn(tabClass, 'text-slate-400')}
          >
            <ChevronLeft aria-hidden className="size-5" strokeWidth={1.8} />
            <span>메인</span>
          </button>
          {extTabs.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              onClick={onNavigate}
              aria-current={section === item.key ? 'page' : false}
              className={cn(tabClass, section === item.key ? 'text-slate-900' : 'text-slate-400')}
            >
              <span className="relative">
                <item.icon
                  aria-hidden
                  className="size-5"
                  strokeWidth={section === item.key ? 2.4 : 1.8}
                />
                <Badge
                  count={item.badge}
                  className="absolute -right-[10px] -top-[9px] h-[16px] min-w-[16px] px-[4px] text-[9px] ring-2 ring-surface"
                />
              </span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </nav>
  )
}
